type AnyFunction = (...args: never[]) => unknown;

type DebounceFunction<T extends AnyFunction> = {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: () => void;
};

const debounce = <T extends AnyFunction>(
  callback: T,
  delay: number,
): DebounceFunction<T> => {
  let timeout: NodeJS.Timeout | undefined;

  let lastArgs: Parameters<T> | undefined;

  let lastThis: unknown;

  const cancel = () => {
    clearTimeout(timeout);

    timeout = undefined;

    lastArgs = undefined;

    lastThis = undefined;
  };

  const flush = () => {
    if (timeout && lastArgs) {
      clearTimeout(timeout);

      callback.apply(lastThis, lastArgs);

      cancel();
    }
  };

  const debounced = function (this: unknown, ...args: Parameters<T>) {
    lastArgs = args;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      callback.apply(lastThis, args);

      timeout = undefined;
    }, delay);
  };

  debounced.cancel = cancel;

  debounced.flush = flush;

  return debounced as DebounceFunction<T>;
};

export { debounce };
